import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowRight, ArrowLeft, Check, Upload, MapPin, CalendarDays, Package,
  Phone, Mail, User, Sparkles, ExternalLink, CheckCircle2,
  Search, X, Recycle, Info, Loader2, MessageCircle, Image as ImageIcon,
  Sofa, Refrigerator, ShieldCheck, Shield, Plus, Minus, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix default Leaflet marker icons in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Brand palette constants (from design_guidelines.md)
const BRAND = {
  municipal: '#374B67',
  info: '#69C7EA',
  cta: '#009940',
  ctaHover: '#007F35',
  teal: '#004D5A',
};

const MUEBLES = [
  { id: 'Sofá', label: 'Sofá' },
  { id: 'Mesa', label: 'Mesa' },
  { id: 'Silla', label: 'Silla' },
  { id: 'Armario', label: 'Armario' },
  { id: 'Cama', label: 'Cama' },
  { id: 'Colchón', label: 'Colchón' },
  { id: 'Estantería', label: 'Estantería' },
  { id: 'Mesita de noche', label: 'Mesita de noche' },
  { id: 'Escritorio', label: 'Escritorio' },
  { id: 'Cómoda', label: 'Cómoda' },
  { id: 'Otro', label: 'Otro (especificar)' },
];

const ELECTRO = [
  { id: 'Nevera', label: 'Nevera / Frigorífico' },
  { id: 'Lavadora', label: 'Lavadora' },
  { id: 'Lavavajillas', label: 'Lavavajillas' },
  { id: 'Secadora', label: 'Secadora' },
  { id: 'Microondas', label: 'Microondas' },
  { id: 'Horno', label: 'Horno' },
  { id: 'Televisión', label: 'Televisión' },
  { id: 'Aspiradora', label: 'Aspiradora' },
  { id: 'Aire acondicionado', label: 'Aire acondicionado' },
  { id: 'Otro', label: 'Otro (especificar)' },
];

const STEPS = [
  { id: 0, title: 'Contacto',  subtitle: 'Tus datos',              icon: User },
  { id: 1, title: 'Enseres',   subtitle: 'Qué recogemos',          icon: Package },
  { id: 2, title: 'Ubicación', subtitle: 'Dónde recoger',          icon: MapPin },
  { id: 3, title: 'Fecha',     subtitle: 'Cuándo recoger',         icon: CalendarDays },
  { id: 4, title: 'Foto',      subtitle: 'Opcional · IA Reutiliza', icon: ImageIcon },
];

// Framer Motion variants
const pageVariants = {
  enter: (dir) => ({ x: dir > 0 ? 40 : -40, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] } },
  exit: (dir) => ({ x: dir > 0 ? -40 : 40, opacity: 0, transition: { duration: 0.2 } }),
};

const fmtDateES = (iso) => {
  try {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long' });
  } catch {
    return iso;
  }
};

export default function NuevaSolicitud() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);

  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [loading, setLoading] = useState(false);

  // Disclaimer (initial) + Reutiliza AI modal
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [showReuseModal, setShowReuseModal] = useState(false);
  const [reuseResult, setReuseResult] = useState(null);
  const [analyzingPhoto, setAnalyzingPhoto] = useState(false);

  // Address search
  const [searchAddress, setSearchAddress] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Available dates
  const [datesLoading, setDatesLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);

  const [form, setForm] = useState({
    nombre_completo: '',
    email: '',
    telefono: '',
    has_whatsapp: false,
    privacidad_aceptada: false,
    // Items as map { itemId: quantity } e.g. { 'Sofá': 1, 'Silla': 4 }
    muebles: {},
    electro: {},
    otro_mueble: '',
    otro_mueble_qty: 1,
    otro_electro: '',
    otro_electro_qty: 1,
    detalles: '',
    latitude: 40.4732,
    longitude: -3.8726,
    direccion_completa: '',
    zona: '',
    fecha_preferida: '',
    imagen_base64: null,
    imagen_preview: null,
  });

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Init map when step 2 opens. Because AnimatePresence mode="wait" unmounts the
  // previous step, we must (a) fully dispose prior map instance, and (b) wait
  // for the new <div> ref to be in the DOM with non-zero size.
  useEffect(() => {
    if (step !== 2) {
      // cleanup any previous instance to avoid stale refs
      if (mapInstance.current) {
        try { mapInstance.current.remove(); } catch { /* noop */ }
        mapInstance.current = null;
        markerRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const tryInit = (attempt = 0) => {
      if (cancelled) return;
      const el = mapRef.current;
      if (!el || el.offsetWidth === 0) {
        if (attempt < 20) {
          setTimeout(() => tryInit(attempt + 1), 80);
        }
        return;
      }
      // dispose previous
      if (mapInstance.current) {
        try { mapInstance.current.remove(); } catch { /* noop */ }
        mapInstance.current = null;
      }

      const m = L.map(el, { zoomControl: true }).setView(
        [form.latitude || 40.4732, form.longitude || -3.8726],
        15
      );
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        maxZoom: 19,
      }).addTo(m);

      const marker = L.marker([form.latitude || 40.4732, form.longitude || -3.8726], {
        draggable: true,
      }).addTo(m);
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        reverseGeocode(pos.lat, pos.lng);
      });
      m.on('click', (e) => {
        marker.setLatLng(e.latlng);
        reverseGeocode(e.latlng.lat, e.latlng.lng);
      });

      mapInstance.current = m;
      markerRef.current = marker;
      setTimeout(() => m.invalidateSize(), 120);
    };

    // Wait for the AnimatePresence transition (~280ms) before init.
    const t = setTimeout(() => tryInit(), 320);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Load available dates when entering date step
  useEffect(() => {
    if (step === 3) loadDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const loadDates = async () => {
    setDatesLoading(true);
    try {
      const resp = await axios.get(`${API}/fechas-disponibles`);
      setAvailableDates(resp.data.available_dates || []);
    } catch (err) {
      toast.error('No se pudieron cargar las fechas disponibles');
    } finally {
      setDatesLoading(false);
    }
  };

  // Extract a human-friendly "zona" from a Nominatim address object
  const extractZona = (addr) => {
    if (!addr) return '';
    const keys = ['neighbourhood', 'suburb', 'quarter', 'city_district', 'residential', 'hamlet', 'village', 'town', 'city'];
    for (const k of keys) {
      if (addr[k]) return String(addr[k]).trim();
    }
    return '';
  };

  const reverseGeocode = async (lat, lng) => {
    update({ latitude: lat, longitude: lng, direccion_completa: `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
    try {
      const resp = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { 'Accept-Language': 'es' } }
      );
      if (resp.data && resp.data.display_name) {
        const z = extractZona(resp.data.address);
        update({ direccion_completa: resp.data.display_name, zona: z || '' });
      }
    } catch {
      /* ignore */
    }
  };

  const handleSearchAddress = async () => {
    if (!searchAddress.trim()) return;
    setSearching(true);
    setSearchResults([]);
    setShowSearchResults(false);
    try {
      const resp = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchAddress + ', Majadahonda, Madrid'
        )}&limit=6&addressdetails=1&countrycodes=es`,
        { headers: { 'Accept-Language': 'es' } }
      );
      if (resp.data && resp.data.length > 0) {
        setSearchResults(resp.data);
        setShowSearchResults(true);
        // Auto-place only if exactly 1 result to avoid guessing
        if (resp.data.length === 1) {
          handleSelectSearchResult(resp.data[0]);
        }
      } else {
        toast.error('No se encontró la dirección. Prueba con otro formato: "Calle Mayor 12" o "Av. España 1"');
      }
    } catch {
      toast.error('Error al buscar dirección');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectSearchResult = (r) => {
    const lat = parseFloat(r.lat);
    const lon = parseFloat(r.lon);
    if (mapInstance.current && markerRef.current) {
      mapInstance.current.setView([lat, lon], 18);
      markerRef.current.setLatLng([lat, lon]);
    }
    const z = extractZona(r.address);
    update({ latitude: lat, longitude: lon, direccion_completa: r.display_name, zona: z || '' });
    setShowSearchResults(false);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen supera 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      update({ imagen_base64: base64, imagen_preview: base64 });
      // Fire AI analysis
      setAnalyzingPhoto(true);
      try {
        const tipo = selectedItemsText();
        const resp = await axios.post(`${API}/analizar-foto-reutilizacion`, {
          foto_url: base64,
          tipo_enseres: tipo || 'Enseres',
        });
        if (resp.data?.puede_reutilizar && (resp.data.confianza ?? 0) >= 60) {
          setReuseResult(resp.data);
          setShowReuseModal(true);
        } else {
          toast.success('Foto cargada correctamente');
        }
      } catch {
        /* silent */
      } finally {
        setAnalyzingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const selectedItemsText = () => {
    const fmt = (id, qty) => (qty > 1 ? `${id} (x${qty})` : id);
    const muebles = Object.entries(form.muebles)
      .filter(([id, q]) => q > 0 && id !== 'Otro')
      .map(([id, q]) => fmt(id, q));
    if (form.muebles['Otro'] && form.otro_mueble) {
      muebles.push(fmt(form.otro_mueble, form.otro_mueble_qty || 1));
    }
    const electro = Object.entries(form.electro)
      .filter(([id, q]) => q > 0 && id !== 'Otro')
      .map(([id, q]) => fmt(id, q));
    if (form.electro['Otro'] && form.otro_electro) {
      electro.push(fmt(form.otro_electro, form.otro_electro_qty || 1));
    }
    return [...muebles, ...electro].filter(Boolean).join(', ');
  };

  const selectedItemsList = useMemo(() => {
    const fmt = (id, qty) => (qty > 1 ? `${id} (x${qty})` : id);
    const muebles = Object.entries(form.muebles)
      .filter(([id, q]) => q > 0 && id !== 'Otro')
      .map(([id, q]) => fmt(id, q));
    if (form.muebles['Otro'] && form.otro_mueble) {
      muebles.push(fmt(form.otro_mueble, form.otro_mueble_qty || 1));
    }
    const electro = Object.entries(form.electro)
      .filter(([id, q]) => q > 0 && id !== 'Otro')
      .map(([id, q]) => fmt(id, q));
    if (form.electro['Otro'] && form.otro_electro) {
      electro.push(fmt(form.otro_electro, form.otro_electro_qty || 1));
    }
    return [...muebles, ...electro].filter(Boolean);
  }, [form.muebles, form.electro, form.otro_mueble, form.otro_electro, form.otro_mueble_qty, form.otro_electro_qty]);

  const totalUnidades = useMemo(() => {
    const sumGroup = (g, otroQty, otroVal) => {
      let s = 0;
      Object.entries(g).forEach(([id, q]) => {
        if (id === 'Otro' && otroVal) s += otroQty || 1;
        else if (id !== 'Otro') s += q || 0;
      });
      return s;
    };
    return (
      sumGroup(form.muebles, form.otro_mueble_qty, form.otro_mueble) +
      sumGroup(form.electro, form.otro_electro_qty, form.otro_electro)
    );
  }, [form.muebles, form.electro, form.otro_mueble, form.otro_electro, form.otro_mueble_qty, form.otro_electro_qty]);

  const toggleItem = (group, id) => {
    setForm((prev) => {
      const cur = prev[group];
      const next = { ...cur };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = 1;
      }
      return { ...prev, [group]: next };
    });
  };

  const setItemQty = (group, id, qty) => {
    const v = Math.max(1, Math.min(50, parseInt(qty || 1, 10) || 1));
    setForm((prev) => ({ ...prev, [group]: { ...prev[group], [id]: v } }));
  };
  const incItemQty = (group, id, delta) => {
    setForm((prev) => {
      const cur = prev[group][id] || 1;
      const v = Math.max(1, Math.min(50, cur + delta));
      return { ...prev, [group]: { ...prev[group], [id]: v } };
    });
  };

  const handleGoReutiliza = () => {
    window.open('https://reutiliza-majadahonda.com/', '_blank', 'noopener,noreferrer');
    setShowReuseModal(false);
  };

  const validateStep = () => {
    if (step === 0) {
      if (!form.nombre_completo.trim()) { toast.error('El nombre completo es obligatorio'); return false; }
      if (!form.email && !form.telefono) { toast.error('Indica al menos un email o un teléfono'); return false; }
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error('El email no es válido'); return false; }
      if (form.telefono && form.telefono.replace(/\D/g, '').length < 9) { toast.error('El teléfono debe tener al menos 9 dígitos'); return false; }
    }
    if (step === 1) {
      if (selectedItemsList.length === 0) { toast.error('Selecciona al menos un enser'); return false; }
      if (form.muebles['Otro'] !== undefined && !form.otro_mueble.trim()) { toast.error('Especifica el mueble “Otro”'); return false; }
      if (form.electro['Otro'] !== undefined && !form.otro_electro.trim()) { toast.error('Especifica el electrodoméstico “Otro”'); return false; }
      if (totalUnidades > 6) { toast.error('El máximo es 6 enseres por solicitud. Si tienes más, contacta directamente con el servicio.'); return false; }
    }
    if (step === 2) {
      if (!form.direccion_completa) { toast.error('Marca la ubicación en el mapa'); return false; }
      // Validate that address includes a house/building number
      if (!/,\s*\d+/.test(form.direccion_completa) && !/\s\d+[,\s]/.test(form.direccion_completa)) {
        toast.error('La dirección debe incluir el número de portal o edificio (ej: Calle Mayor, 12)');
        return false;
      }
    }
    if (step === 3) {
      if (!form.fecha_preferida) { toast.error('Selecciona una fecha disponible'); return false; }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setDirection(1);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => {
    setDirection(-1);
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const payload = {
        recibida_por: 'Portal Web',
        nombre_completo: form.nombre_completo.trim(),
        email: form.email.trim() || null,
        telefono: form.telefono.trim() || null,
        has_whatsapp: !!form.has_whatsapp,
        canal_respuesta: form.email ? 'email' : 'whatsapp',
        fecha_preferida: form.fecha_preferida,
        tipo_enseres: selectedItemsText(),
        enseres_lista: selectedItemsList,
        detalles: form.detalles.trim(),
        latitude: form.latitude,
        longitude: form.longitude,
        direccion_completa: form.direccion_completa,
        direccion_aprox: (form.direccion_completa || '').split(',')[0] || '',
        zona: form.zona || null,
        imagen_base64: form.imagen_base64,
      };
      const resp = await axios.post(`${API}/solicitudes`, payload);
      toast.success('¡Solicitud creada con éxito!');
      const ticketCode = resp.data?.ticket_code;
      if (ticketCode) {
        navigate(`/confirmacion/${ticketCode}`, { state: { ticket: resp.data } });
      } else {
        navigate('/');
      }
    } catch (err) {
      const msg = err.response?.data?.detail;
      if (Array.isArray(msg)) {
        toast.error(msg.map((m) => m.msg).join(' · '));
      } else {
        toast.error(msg || 'Error al crear la solicitud');
      }
    } finally {
      setLoading(false);
    }
  };

  const progressPct = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen relative bg-[#F7F9FC]">
      {/* Decorative background: radial gradients + subtle noise */}
      <div className="pointer-events-none absolute inset-0 -z-0"
        style={{
          backgroundImage:
            'radial-gradient(900px circle at 15% 10%, rgba(105,199,234,0.35), transparent 55%), radial-gradient(700px circle at 85% 0%, rgba(0,153,64,0.12), transparent 50%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 -z-0 opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.55'/></svg>\")",
        }}
      />

      <Header />

      {/* ─── DISCLAIMER MODAL ─── */}
      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent className="sm:max-w-lg" data-testid="reutiliza-disclaimer-modal">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-[#009940] to-[#00CC52] flex items-center justify-center shadow-lg mb-2">
              <Recycle size={28} className="text-white" />
            </div>
            <DialogTitle className="text-center text-xl text-[#374B67]">
              Antes de continuar… ¿tu enser está en buen estado?
            </DialogTitle>
            <DialogDescription className="text-center text-slate-600 pt-1">
              Si aún es útil, puedes darle una <strong className="text-[#009940]">segunda vida</strong> a
              través de la plataforma <strong>Reutiliza Majadahonda</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="rounded-xl border border-[#009940]/20 bg-gradient-to-br from-[#009940]/5 to-[#69C7EA]/5 p-4">
              <ul className="text-sm text-slate-700 space-y-2">
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#009940] mt-0.5 flex-shrink-0" /><span>Reduce residuos y cuida el medioambiente.</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#009940] mt-0.5 flex-shrink-0" /><span>Otros vecinos pueden aprovecharlo.</span></li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#009940] mt-0.5 flex-shrink-0" /><span>Es rápido y gratuito.</span></li>
              </ul>
            </div>
            <Button
              type="button"
              onClick={() => { window.open('https://reutiliza-majadahonda.com/', '_blank', 'noopener,noreferrer'); setShowDisclaimer(false); }}
              className="w-full bg-[#009940] hover:bg-[#007F35] text-white rounded-xl"
              data-testid="reutiliza-disclaimer-open-button"
            >
              <ExternalLink size={16} className="mr-2" />
              Ir a Reutiliza Majadahonda
            </Button>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDisclaimer(false)}
              className="w-full rounded-xl border-slate-200"
              data-testid="reutiliza-disclaimer-continue-button"
            >
              Continuar con la solicitud de recogida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── IA REUSE MODAL ─── */}
      <Dialog open={showReuseModal} onOpenChange={setShowReuseModal}>
        <DialogContent className="sm:max-w-lg" data-testid="reutiliza-ai-modal">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-[#004D5A] to-[#009940] flex items-center justify-center shadow-lg mb-2">
              <Sparkles size={26} className="text-white" />
            </div>
            <DialogTitle className="text-center text-xl text-[#374B67]">
              ¡Parece que aún tiene vida útil!
            </DialogTitle>
            <DialogDescription className="text-center text-slate-600 pt-1">
              Nuestra IA ha analizado tu foto y detecta que el enser podría reutilizarse.
            </DialogDescription>
          </DialogHeader>
          {reuseResult && (
            <div className="space-y-3">
              <div className="rounded-xl border border-green-200 bg-green-50/80 backdrop-blur-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-900">Confianza del análisis</span>
                  <Badge className="bg-[#009940] hover:bg-[#007F35] text-white border-0">
                    {reuseResult.confianza}%
                  </Badge>
                </div>
                <div className="h-2 bg-green-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${reuseResult.confianza}%` }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full bg-gradient-to-r from-[#009940] to-[#00CC52]"
                  />
                </div>
                <p className="text-sm text-green-900 mt-3">{reuseResult.razon}</p>
              </div>
              <Button
                onClick={handleGoReutiliza}
                className="w-full bg-[#009940] hover:bg-[#007F35] text-white rounded-xl"
                data-testid="reutiliza-ai-open-button"
              >
                <ExternalLink size={16} className="mr-2" />
                Publicar en Reutiliza Majadahonda
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowReuseModal(false)}
              className="w-full rounded-xl border-slate-200"
              data-testid="reutiliza-ai-continue-button"
            >
              Continuar con la recogida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── CONTENT ─── */}
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
        {/* Page heading */}
        <div className="mb-6 lg:mb-10">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur-sm border border-slate-200 px-3 py-1 text-xs text-slate-600 shadow-sm">
              <ShieldCheck size={14} className="text-[#009940]" />
              <span>Portal oficial · Ayuntamiento de Majadahonda</span>
            </div>
            <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#374B67]">
              Solicita la recogida de enseres
            </h1>
            <p className="mt-2 text-slate-600 max-w-2xl">
              Un proceso rápido y guiado. Rellena los datos, marca la ubicación y
              elige la fecha. Nosotros nos encargamos del resto.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          {/* ───── STEPPER (desktop) ───── */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-sm shadow-sm p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
                Pasos de la solicitud
              </p>
              <ol className="space-y-1">
                {STEPS.map((s) => {
                  const Icon = s.icon;
                  const isActive = s.id === step;
                  const isDone = s.id < step;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        disabled={s.id > step}
                        onClick={() => { if (s.id < step) { setDirection(-1); setStep(s.id); } }}
                        className={`w-full group flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors duration-150 ${
                          isActive
                            ? 'bg-[#69C7EA]/15 text-[#374B67]'
                            : isDone
                            ? 'text-slate-700 hover:bg-slate-50'
                            : 'text-slate-400'
                        }`}
                        data-testid={`wizard-step-${s.id}`}
                      >
                        <span
                          className={`flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0 transition-colors duration-150 ${
                            isActive
                              ? 'bg-gradient-to-br from-[#69C7EA] to-[#5AB5D8] text-white shadow-md'
                              : isDone
                              ? 'bg-[#009940] text-white'
                              : 'bg-slate-100 text-slate-400'
                          }`}
                        >
                          {isDone ? <Check size={16} /> : <Icon size={16} />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className={`block text-sm font-medium ${isActive ? 'text-[#374B67]' : ''}`}>{s.title}</span>
                          <span className="block text-xs text-slate-500 truncate">{s.subtitle}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>

              <div className="mt-5 pt-5 border-t border-slate-200">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                  <span>Progreso</span>
                  <span className="font-semibold text-[#374B67]">{Math.round(progressPct)}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#009940] to-[#69C7EA]"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
              </div>
            </div>
          </aside>

          {/* ───── CONTENT CARD ───── */}
          <section>
            {/* Mobile progress */}
            <div className="lg:hidden mb-4">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                <span className="font-medium text-[#374B67]">Paso {step + 1} de {STEPS.length}: {STEPS[step].title}</span>
                <span>{Math.round(progressPct)}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#009940] to-[#69C7EA]"
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/85 backdrop-blur-md shadow-sm overflow-hidden">
              <AnimatePresence custom={direction} mode="wait">
                <motion.div
                  key={step}
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="p-5 sm:p-8 min-h-[520px]"
                >
                  {/* STEP 0 — CONTACT */}
                  {step === 0 && (
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-2xl font-semibold text-[#374B67]">Datos de contacto</h2>
                        <p className="text-sm text-slate-600 mt-1">Te avisaremos por email o WhatsApp de cada cambio de estado.</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="nombre" className="text-sm font-medium text-slate-700">
                          Nombre completo <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                          <Input
                            id="nombre"
                            value={form.nombre_completo}
                            onChange={(e) => update({ nombre_completo: e.target.value })}
                            placeholder="Juan Pérez García"
                            className="pl-9 h-11 rounded-xl"
                            data-testid="wizard-input-nombre"
                            autoFocus
                          />
                        </div>
                      </div>

                      <div className="rounded-xl border border-amber-200 bg-amber-50/80 backdrop-blur-sm p-3 flex items-start gap-3">
                        <Info size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-900">
                          Indica al menos <strong>un medio de contacto</strong>: email o teléfono.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
                          <div className="relative">
                            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                              id="email"
                              type="email"
                              value={form.email}
                              onChange={(e) => update({ email: e.target.value })}
                              placeholder="correo@ejemplo.com"
                              className="pl-9 h-11 rounded-xl"
                              data-testid="wizard-input-email"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="telefono" className="text-sm font-medium text-slate-700">Teléfono</Label>
                          <div className="relative">
                            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                              id="telefono"
                              type="tel"
                              value={form.telefono}
                              onChange={(e) => update({ telefono: e.target.value })}
                              placeholder="600 123 456"
                              className="pl-9 h-11 rounded-xl"
                              data-testid="wizard-input-telefono"
                            />
                          </div>
                        </div>
                      </div>

                      <label
                        htmlFor="whatsapp-chk"
                        className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-150 ${
                          form.has_whatsapp ? 'border-[#009940] bg-[#009940]/5' : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <Checkbox
                          id="whatsapp-chk"
                          checked={form.has_whatsapp}
                          onCheckedChange={(v) => update({ has_whatsapp: !!v })}
                          data-testid="wizard-checkbox-whatsapp"
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <MessageCircle size={18} className="text-[#009940]" />
                          <div>
                            <p className="text-sm font-medium text-slate-800">Este teléfono tiene WhatsApp</p>
                            <p className="text-xs text-slate-500">Recibirás notificaciones automáticas.</p>
                          </div>
                        </div>
                      </label>

                      {/* ─── RGPD CONSENT ─── */}
                      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
                        <div className="flex items-start gap-2 text-xs text-slate-500">
                          <Shield size={14} className="text-[#374B67] mt-0.5 flex-shrink-0" />
                          <p>
                            <strong className="text-slate-700">Protección de datos (RGPD)</strong> — Sus datos se
                            tratarán bajo la responsabilidad del Ayuntamiento de Majadahonda, exclusivamente
                            para gestionar su solicitud de recogida. Puede ejercer sus derechos ARCOP en{' '}
                            <a href="mailto:dpd@majadahonda.es" className="text-[#374B67] underline">dpd@majadahonda.es</a>.
                          </p>
                        </div>
                        <label
                          htmlFor="privacidad-chk"
                          className={`flex items-start gap-3 cursor-pointer rounded-lg border p-3 transition-colors duration-150 ${
                            form.privacidad_aceptada
                              ? 'border-[#374B67] bg-[#374B67]/5'
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          <Checkbox
                            id="privacidad-chk"
                            checked={form.privacidad_aceptada}
                            onCheckedChange={(v) => update({ privacidad_aceptada: !!v })}
                            data-testid="wizard-checkbox-privacidad"
                            className="mt-0.5"
                          />
                          <span className="text-sm text-slate-700 leading-snug">
                            He leído y acepto la{' '}
                            <a
                              href="/privacidad"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#374B67] font-medium underline underline-offset-2 hover:text-[#2A3B52]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Política de Privacidad
                            </a>{' '}
                            y autorizo el tratamiento de mis datos para la gestión de esta solicitud.{' '}
                            <span className="text-red-500 font-medium">*</span>
                          </span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* STEP 1 — ITEMS */}
                  {step === 1 && (
                    <div className="space-y-6">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <h2 className="text-2xl font-semibold text-[#374B67]">¿Qué necesitas que recojamos?</h2>
                          <p className="text-sm text-slate-600 mt-1">
                            Selecciona los enseres y ajusta la cantidad de cada uno (ej: 4 sillas).
                          </p>
                        </div>
                        {totalUnidades > 0 && (
                          <div
                            className={`rounded-xl border px-4 py-2 text-center ${totalUnidades >= 6 ? 'border-amber-300 bg-amber-50' : 'border-[#009940]/20 bg-[#009940]/5'}`}
                            data-testid="items-total-badge"
                          >
                            <p className="text-xs text-slate-500 uppercase tracking-wider">Total</p>
                            <p className={`text-xl font-bold ${totalUnidades >= 6 ? 'text-amber-600' : 'text-[#009940]'}`}>
                              {totalUnidades} <span className="text-xs font-medium">/ 6 max</span>
                            </p>
                            {totalUnidades >= 6 && (
                              <p className="text-xs text-amber-600 mt-0.5">Límite alcanzado</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Muebles */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Sofa size={18} className="text-[#374B67]" />
                          <h3 className="text-sm font-semibold text-[#374B67]">Muebles</h3>
                          {Object.keys(form.muebles).length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {Object.keys(form.muebles).length} tipos
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                          {MUEBLES.map((m) => {
                            const qty = form.muebles[m.id];
                            const active = qty !== undefined;
                            return (
                              <motion.div
                                key={m.id}
                                whileHover={!active ? { scale: 1.02 } : {}}
                                className={`rounded-xl border-2 transition-colors duration-150 overflow-hidden ${
                                  active
                                    ? 'border-[#009940] bg-[#009940]/5'
                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() => toggleItem('muebles', m.id)}
                                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-left ${
                                    active ? 'text-[#007F35]' : 'text-slate-700'
                                  }`}
                                  data-testid={`item-mueble-${m.id}`}
                                >
                                  <span className={`flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-colors ${
                                    active ? 'bg-[#009940] text-white' : 'border border-slate-300 bg-white'
                                  }`}>
                                    {active && <Check size={12} />}
                                  </span>
                                  <span className="flex-1 truncate">{m.label}</span>
                                </button>
                                <AnimatePresence>
                                  {active && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      className="border-t border-[#009940]/15 bg-white/60 backdrop-blur-sm overflow-hidden"
                                    >
                                      <div className="flex items-center justify-between px-2 py-1.5">
                                        <span className="text-xs text-slate-500 pl-1">Cantidad</span>
                                        <div className="flex items-center gap-1">
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); incItemQty('muebles', m.id, -1); }}
                                            disabled={qty <= 1}
                                            className="w-7 h-7 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            data-testid={`qty-mueble-${m.id}-minus`}
                                          >
                                            <Minus size={13} />
                                          </button>
                                          <input
                                            type="number"
                                            min="1"
                                            max="50"
                                            value={qty}
                                            onChange={(e) => setItemQty('muebles', m.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-10 h-7 text-center text-sm font-semibold text-[#374B67] bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-[#69C7EA] rounded"
                                            data-testid={`qty-mueble-${m.id}-input`}
                                          />
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); incItemQty('muebles', m.id, +1); }}
                                            disabled={qty >= 50}
                                            className="w-7 h-7 rounded-md border border-[#009940]/30 bg-[#009940]/10 flex items-center justify-center text-[#009940] hover:bg-[#009940]/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            data-testid={`qty-mueble-${m.id}-plus`}
                                          >
                                            <Plus size={13} />
                                          </button>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </motion.div>
                            );
                          })}
                        </div>
                        <AnimatePresence>
                          {form.muebles['Otro'] !== undefined && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="flex gap-2 mt-3">
                                <Input
                                  value={form.otro_mueble}
                                  onChange={(e) => update({ otro_mueble: e.target.value })}
                                  placeholder="Especifica qué mueble…"
                                  className="flex-1 h-11 rounded-xl"
                                  data-testid="wizard-input-otro-mueble"
                                />
                                <div className="flex items-center gap-1 border border-slate-200 rounded-xl bg-white px-1">
                                  <button
                                    type="button"
                                    onClick={() => update({ otro_mueble_qty: Math.max(1, (form.otro_mueble_qty || 1) - 1) })}
                                    className="w-8 h-9 flex items-center justify-center text-slate-600 hover:bg-slate-50 rounded-lg disabled:opacity-40"
                                    disabled={(form.otro_mueble_qty || 1) <= 1}
                                  >
                                    <Minus size={14} />
                                  </button>
                                  <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={form.otro_mueble_qty || 1}
                                    onChange={(e) => update({ otro_mueble_qty: Math.max(1, Math.min(50, parseInt(e.target.value || '1', 10))) })}
                                    className="w-10 h-9 text-center text-sm font-semibold text-[#374B67] bg-transparent border-0 focus:outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => update({ otro_mueble_qty: Math.min(50, (form.otro_mueble_qty || 1) + 1) })}
                                    className="w-8 h-9 flex items-center justify-center text-[#009940] hover:bg-[#009940]/10 rounded-lg"
                                  >
                                    <Plus size={14} />
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Electrodomésticos */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Refrigerator size={18} className="text-[#374B67]" />
                          <h3 className="text-sm font-semibold text-[#374B67]">Electrodomésticos</h3>
                          {Object.keys(form.electro).length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {Object.keys(form.electro).length} tipos
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                          {ELECTRO.map((m) => {
                            const qty = form.electro[m.id];
                            const active = qty !== undefined;
                            return (
                              <motion.div
                                key={m.id}
                                whileHover={!active ? { scale: 1.02 } : {}}
                                className={`rounded-xl border-2 transition-colors duration-150 overflow-hidden ${
                                  active
                                    ? 'border-[#69C7EA] bg-[#69C7EA]/10'
                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() => toggleItem('electro', m.id)}
                                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-left ${
                                    active ? 'text-[#374B67]' : 'text-slate-700'
                                  }`}
                                  data-testid={`item-electro-${m.id}`}
                                >
                                  <span className={`flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center transition-colors ${
                                    active ? 'bg-[#69C7EA] text-white' : 'border border-slate-300 bg-white'
                                  }`}>
                                    {active && <Check size={12} />}
                                  </span>
                                  <span className="flex-1 truncate">{m.label}</span>
                                </button>
                                <AnimatePresence>
                                  {active && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      className="border-t border-[#69C7EA]/30 bg-white/60 backdrop-blur-sm overflow-hidden"
                                    >
                                      <div className="flex items-center justify-between px-2 py-1.5">
                                        <span className="text-xs text-slate-500 pl-1">Cantidad</span>
                                        <div className="flex items-center gap-1">
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); incItemQty('electro', m.id, -1); }}
                                            disabled={qty <= 1}
                                            className="w-7 h-7 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            data-testid={`qty-electro-${m.id}-minus`}
                                          >
                                            <Minus size={13} />
                                          </button>
                                          <input
                                            type="number"
                                            min="1"
                                            max="50"
                                            value={qty}
                                            onChange={(e) => setItemQty('electro', m.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-10 h-7 text-center text-sm font-semibold text-[#374B67] bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-[#69C7EA] rounded"
                                            data-testid={`qty-electro-${m.id}-input`}
                                          />
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); incItemQty('electro', m.id, +1); }}
                                            disabled={qty >= 50}
                                            className="w-7 h-7 rounded-md border border-[#69C7EA]/40 bg-[#69C7EA]/15 flex items-center justify-center text-[#374B67] hover:bg-[#69C7EA]/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            data-testid={`qty-electro-${m.id}-plus`}
                                          >
                                            <Plus size={13} />
                                          </button>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </motion.div>
                            );
                          })}
                        </div>
                        <AnimatePresence>
                          {form.electro['Otro'] !== undefined && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="flex gap-2 mt-3">
                                <Input
                                  value={form.otro_electro}
                                  onChange={(e) => update({ otro_electro: e.target.value })}
                                  placeholder="Especifica qué electrodoméstico…"
                                  className="flex-1 h-11 rounded-xl"
                                  data-testid="wizard-input-otro-electro"
                                />
                                <div className="flex items-center gap-1 border border-slate-200 rounded-xl bg-white px-1">
                                  <button
                                    type="button"
                                    onClick={() => update({ otro_electro_qty: Math.max(1, (form.otro_electro_qty || 1) - 1) })}
                                    className="w-8 h-9 flex items-center justify-center text-slate-600 hover:bg-slate-50 rounded-lg disabled:opacity-40"
                                    disabled={(form.otro_electro_qty || 1) <= 1}
                                  >
                                    <Minus size={14} />
                                  </button>
                                  <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={form.otro_electro_qty || 1}
                                    onChange={(e) => update({ otro_electro_qty: Math.max(1, Math.min(50, parseInt(e.target.value || '1', 10))) })}
                                    className="w-10 h-9 text-center text-sm font-semibold text-[#374B67] bg-transparent border-0 focus:outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => update({ otro_electro_qty: Math.min(50, (form.otro_electro_qty || 1) + 1) })}
                                    className="w-8 h-9 flex items-center justify-center text-[#69C7EA] hover:bg-[#69C7EA]/10 rounded-lg"
                                  >
                                    <Plus size={14} />
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="detalles" className="text-sm font-medium text-slate-700">
                          Instrucciones de recogida <span className="text-slate-400 text-xs font-normal">(opcional)</span>
                        </Label>
                        <Textarea
                          id="detalles"
                          value={form.detalles}
                          onChange={(e) => update({ detalles: e.target.value })}
                          placeholder="¿Dónde estarán los enseres? Portal, escalera, piso, estado… (opcional)"
                          rows={3}
                          className="rounded-xl resize-none"
                          data-testid="wizard-input-detalles"
                        />
                        <div className="flex items-center justify-end text-xs">
                          <span className="text-slate-400">{form.detalles.length} / 2000</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 2 — LOCATION */}
                  {step === 2 && (
                    <div className="space-y-4">
                      <div>
                        <h2 className="text-2xl font-semibold text-[#374B67]">Ubicación de recogida</h2>
                        <p className="text-sm text-slate-600 mt-1">Busca tu dirección con <strong>número de portal</strong> y selecciona el resultado correcto.</p>
                      </div>

                      {/* Search bar + results dropdown */}
                      <div className="relative">
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                              value={searchAddress}
                              onChange={(e) => { setSearchAddress(e.target.value); setShowSearchResults(false); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchAddress(); } }}
                              placeholder="Ej: Calle San Andrés 43"
                              className="pl-9 h-11 rounded-xl"
                              data-testid="wizard-input-address-search"
                            />
                          </div>
                          <Button
                            onClick={handleSearchAddress}
                            disabled={searching}
                            className="h-11 px-5 rounded-xl bg-[#69C7EA] hover:bg-[#5AB5D8] text-[#374B67] font-medium"
                            data-testid="wizard-search-address-button"
                          >
                            {searching ? <Loader2 size={16} className="animate-spin" /> : 'Buscar'}
                          </Button>
                        </div>

                        {/* Results dropdown */}
                        <AnimatePresence>
                          {showSearchResults && searchResults.length > 0 && (
                            <motion.div
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.15 }}
                              className="absolute z-[1000] left-0 right-0 mt-1 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden"
                            >
                              <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
                                <Info size={13} className="text-amber-600 flex-shrink-0" />
                                <p className="text-xs text-amber-800">
                                  {searchResults.length === 1
                                    ? 'Se encontró 1 resultado — selecciónalo para colocar el marcador'
                                    : `Se encontraron ${searchResults.length} resultados — elige el que corresponda a tu portal`}
                                </p>
                              </div>
                              <ul>
                                {searchResults.map((r, idx) => (
                                  <li key={idx}>
                                    <button
                                      type="button"
                                      onClick={() => handleSelectSearchResult(r)}
                                      className="w-full text-left px-4 py-3 hover:bg-[#69C7EA]/10 flex items-start gap-3 border-b border-slate-100 last:border-0 transition-colors"
                                      data-testid={`search-result-${idx}`}
                                    >
                                      <MapPin size={15} className="text-[#374B67] flex-shrink-0 mt-0.5" />
                                      <div className="min-w-0">
                                        <p className="text-sm text-slate-800 leading-snug truncate">{r.display_name}</p>
                                        <p className="text-xs text-slate-400 mt-0.5 font-mono">
                                          {parseFloat(r.lat).toFixed(5)}, {parseFloat(r.lon).toFixed(5)}
                                        </p>
                                      </div>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                              <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
                                <p className="text-xs text-slate-500">
                                  ¿No aparece tu portal? Coloca el marcador directamente en el mapa arrastrándolo.
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      <div
                        className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm relative"
                        style={{ height: 400 }}
                      >
                        <div ref={mapRef} className="w-full h-full" data-testid="location-picker-map" />
                        {/* Drag-to-adjust tip — always visible on map */}
                        <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-xl shadow-md text-xs text-slate-700 flex items-center gap-2 border border-slate-200">
                          <MapPin size={14} className="text-[#374B67] flex-shrink-0" />
                          <span><strong>¿El marcador azul no está en tu portal?</strong> Arrástralo o haz clic en el punto exacto del mapa.</span>
                        </div>
                      </div>

                      {form.direccion_completa && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-2"
                        >
                          {/* Orange accuracy warning */}
                          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3">
                            <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-amber-900 leading-snug">
                              <strong>Comprueba que el marcador azul del mapa está en tu portal exacto.</strong>{' '}
                              Si no es correcto, arrástralo o haz clic en el punto exacto antes de continuar.
                            </p>
                          </div>
                          {/* Address card */}
                          <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur-sm p-4 flex items-start gap-3 shadow-sm">
                            <div className="w-9 h-9 rounded-lg bg-[#69C7EA]/20 flex items-center justify-center flex-shrink-0">
                              <MapPin size={18} className="text-[#374B67]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ubicación seleccionada</p>
                              <p className="text-sm text-slate-800 mt-0.5 break-words" data-testid="location-picker-address">
                                {form.direccion_completa}
                              </p>
                              {form.zona && (
                                <p className="text-xs text-[#374B67] mt-1">
                                  <span className="text-slate-500">Zona:</span> <strong>{form.zona}</strong>
                                </p>
                              )}
                              <p className="text-xs text-slate-500 mt-1 font-mono">
                                {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  )}

                  {/* STEP 3 — DATE */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-2xl font-semibold text-[#374B67]">Fecha de recogida</h2>
                        <p className="text-sm text-slate-600 mt-1">
                          Elige entre las fechas disponibles. Capacidad máxima de <strong>25 recogidas por día</strong>.
                        </p>
                      </div>

                      {datesLoading ? (
                        <div className="flex items-center justify-center py-16 text-slate-500">
                          <Loader2 size={20} className="animate-spin mr-2" /> Cargando fechas disponibles…
                        </div>
                      ) : availableDates.length === 0 ? (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center space-y-3">
                          <p className="text-sm text-amber-900">
                            No se pudieron cargar las fechas disponibles. Comprueba tu conexión.
                          </p>
                          <Button
                            type="button"
                            onClick={loadDates}
                            variant="outline"
                            className="rounded-xl"
                            data-testid="reload-dates-button"
                          >
                            <RefreshCw size={14} className="mr-2" /> Reintentar
                          </Button>
                        </div>
                      ) : (
                        <>
                          {/* Legend */}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                            <span className="font-medium text-slate-600">Disponibilidad:</span>
                            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full bg-[#009940]" />Alta (&gt;15 plazas)</span>
                            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full bg-amber-400" />Media (6–15)</span>
                            <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full bg-red-500" />Últimas plazas (≤5)</span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {availableDates.map((f) => {
                              const selected = form.fecha_preferida === f.date;
                              const slots = f.available_slots;
                              const MAX_DAY = 25;
                              const taken = MAX_DAY - slots;
                              const pctFull = Math.round((taken / MAX_DAY) * 100);
                              const urgency =
                                slots > 15 ? 'high' :
                                slots > 5  ? 'mid'  : 'low';
                              const urgencyConfig = {
                                high: {
                                  bar: '#009940',
                                  badge: 'bg-green-100 text-green-800 border-green-200',
                                  label: `${slots} plazas libres`,
                                  dot: 'bg-[#009940]',
                                  border: selected ? 'border-[#009940]' : 'border-slate-200 hover:border-green-300',
                                },
                                mid: {
                                  bar: '#F59E0B',
                                  badge: 'bg-amber-100 text-amber-800 border-amber-200',
                                  label: `${slots} plazas libres`,
                                  dot: 'bg-amber-400',
                                  border: selected ? 'border-[#009940]' : 'border-amber-200 hover:border-amber-300',
                                },
                                low: {
                                  bar: '#EF4444',
                                  badge: 'bg-red-100 text-red-700 border-red-200',
                                  label: `¡Solo ${slots} ${slots === 1 ? 'plaza' : 'plazas'}!`,
                                  dot: 'bg-red-500',
                                  border: selected ? 'border-[#009940]' : 'border-red-200 hover:border-red-300',
                                },
                              }[urgency];

                              return (
                                <motion.button
                                  key={f.date}
                                  type="button"
                                  whileHover={{ y: -2 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => update({ fecha_preferida: f.date })}
                                  className={`relative text-left rounded-2xl border-2 overflow-hidden transition-all duration-150 shadow-sm ${
                                    selected
                                      ? `${urgencyConfig.border} bg-[#009940]/5 shadow-md`
                                      : `${urgencyConfig.border} bg-white hover:shadow-md`
                                  }`}
                                  data-testid={`wizard-date-${f.date}`}
                                >
                                  {/* Selected checkmark */}
                                  {selected && (
                                    <motion.div
                                      layoutId="selected-date"
                                      className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-[#009940] text-white flex items-center justify-center shadow-sm z-10"
                                    >
                                      <Check size={13} />
                                    </motion.div>
                                  )}

                                  <div className="p-4 pb-3">
                                    {/* Day + Urgency badge */}
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                        {f.day_of_week}
                                      </p>
                                      {urgency === 'low' && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 leading-none">
                                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" />
                                          Casi lleno
                                        </span>
                                      )}
                                    </div>

                                    {/* Date */}
                                    <p className="text-base font-semibold text-[#374B67] capitalize leading-tight">
                                      {fmtDateES(f.date)}
                                    </p>

                                    {/* Slots counter */}
                                    <div className="mt-3 flex items-center justify-between">
                                      <div className="flex items-center gap-1.5">
                                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${urgencyConfig.dot}`} />
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${urgencyConfig.badge}`}>
                                          {urgencyConfig.label}
                                        </span>
                                      </div>
                                      <span className="text-[11px] text-slate-400 font-mono">
                                        {taken}/{MAX_DAY}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Occupancy bar */}
                                  <div className="h-1.5 w-full bg-slate-100">
                                    <motion.div
                                      initial={{ width: 0 }}
                                      animate={{ width: `${pctFull}%` }}
                                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
                                      style={{ backgroundColor: urgencyConfig.bar }}
                                      className="h-full rounded-r-full"
                                    />
                                  </div>
                                </motion.button>
                              );
                            })}
                          </div>

                          {/* Summary of selected date */}
                          {form.fecha_preferida && (() => {
                            const sel = availableDates.find(d => d.date === form.fecha_preferida);
                            return sel ? (
                              <motion.div
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="rounded-xl border border-[#009940]/30 bg-[#009940]/5 p-4 flex items-center gap-3"
                                data-testid="selected-date-summary"
                              >
                                <div className="w-9 h-9 rounded-lg bg-[#009940] flex items-center justify-center flex-shrink-0">
                                  <Check size={18} className="text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-[#374B67] capitalize">{fmtDateES(sel.date)}</p>
                                  <p className="text-xs text-slate-500 mt-0.5">
                                    {sel.available_slots} plazas disponibles · {sel.formatted}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => update({ fecha_preferida: '' })}
                                  className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg hover:bg-white/80"
                                  aria-label="Cambiar fecha"
                                >
                                  <X size={16} />
                                </button>
                              </motion.div>
                            ) : null;
                          })()}
                        </>
                      )}
                    </div>
                  )}

                  {/* STEP 4 — PHOTO */}
                  {step === 4 && (
                    <div className="space-y-5">
                      <div>
                        <h2 className="text-2xl font-semibold text-[#374B67]">Foto (opcional)</h2>
                        <p className="text-sm text-slate-600 mt-1">
                          Ayúdanos a estimar mejor el volumen. Nuestra IA revisará si tu enser podría <strong>reutilizarse</strong>.
                        </p>
                      </div>

                      <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white/70 backdrop-blur-sm hover:border-[#69C7EA] transition-colors duration-150">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id="image-upload"
                          data-testid="wizard-input-photo"
                        />
                        <label htmlFor="image-upload" className="block cursor-pointer p-8 text-center">
                          {form.imagen_preview ? (
                            <div className="space-y-4">
                              <div className="relative inline-block">
                                <img
                                  src={form.imagen_preview}
                                  alt="Vista previa"
                                  className="max-h-64 mx-auto rounded-xl shadow-md"
                                />
                                {analyzingPhoto && (
                                  <div className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                    <div className="text-white flex items-center gap-2">
                                      <Sparkles size={18} className="animate-pulse" />
                                      <span className="text-sm font-medium">Analizando con IA…</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="rounded-xl"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    update({ imagen_base64: null, imagen_preview: null });
                                  }}
                                  data-testid="wizard-remove-photo-button"
                                >
                                  <X size={14} className="mr-2" /> Quitar foto
                                </Button>
                                <label htmlFor="image-upload">
                                  <span className="inline-flex items-center px-4 py-2 rounded-xl bg-[#69C7EA]/20 text-[#374B67] text-sm font-medium cursor-pointer hover:bg-[#69C7EA]/30 transition-colors duration-150">
                                    <Upload size={14} className="mr-2" /> Cambiar foto
                                  </span>
                                </label>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#69C7EA]/20 to-[#009940]/10 flex items-center justify-center">
                                <Upload size={26} className="text-[#374B67]" />
                              </div>
                              <div>
                                <p className="text-slate-800 font-medium">Haz clic para subir una foto</p>
                                <p className="text-xs text-slate-500 mt-1">JPG, PNG · Máx 5MB</p>
                              </div>
                            </div>
                          )}
                        </label>
                      </div>

                      <div className="rounded-xl border border-[#004D5A]/20 bg-[#004D5A]/5 p-4 flex items-start gap-3">
                        <Sparkles size={16} className="text-[#004D5A] flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-[#004D5A]">
                          <strong>IA Reutiliza:</strong> Si la foto muestra un enser en buen estado, te sugeriremos publicarlo en
                          <strong> Reutiliza Majadahonda</strong> para darle una segunda vida.
                        </p>
                      </div>

                      {/* Review block */}
                      <div className="rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-sm p-5">
                        <h3 className="text-sm font-semibold text-[#374B67] mb-3">Resumen antes de enviar</h3>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div>
                            <dt className="text-xs text-slate-500">Nombre</dt>
                            <dd className="text-slate-800 truncate">{form.nombre_completo || '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs text-slate-500">Contacto</dt>
                            <dd className="text-slate-800 truncate">{form.email || form.telefono || '—'}</dd>
                          </div>
                          <div className="sm:col-span-2">
                            <dt className="text-xs text-slate-500">Enseres</dt>
                            <dd className="text-slate-800">{selectedItemsList.join(', ') || '—'}</dd>
                          </div>
                          <div className="sm:col-span-2">
                            <dt className="text-xs text-slate-500">Dirección</dt>
                            <dd className="text-slate-800 break-words">{form.direccion_completa || '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs text-slate-500">Fecha</dt>
                            <dd className="text-slate-800 capitalize">
                              {form.fecha_preferida ? fmtDateES(form.fecha_preferida) : '—'}
                            </dd>
                          </div>
                        </dl>
                      </div>

                      {/* Reminder box */}
                      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
                        <span className="text-amber-500 text-lg mt-0.5">⚠️</span>
                        <div>
                          <p className="text-sm font-semibold text-amber-800">Recuerda: saca los enseres la noche anterior</p>
                          <p className="text-xs text-amber-700 mt-0.5">
                            Para garantizar la recogida, deposita los enseres en la vía pública <strong>la noche antes</strong> de la fecha seleccionada ({form.fecha_preferida ? fmtDateES(form.fecha_preferida) : ''}). Recibirás una confirmación por {form.canal_respuesta === 'whatsapp' ? 'WhatsApp' : 'email'}.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Footer nav */}
              <div className="flex items-center gap-3 border-t border-slate-200 bg-slate-50/60 backdrop-blur-sm px-5 sm:px-8 py-4">
                {step > 0 ? (
                  <Button
                    variant="outline"
                    onClick={prev}
                    className="rounded-xl border-slate-200 hover:bg-white"
                    data-testid="wizard-back-button"
                  >
                    <ArrowLeft size={16} className="mr-2" /> Atrás
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/')}
                    className="rounded-xl text-slate-600"
                    data-testid="wizard-cancel-button"
                  >
                    Cancelar
                  </Button>
                )}
                <div className="flex-1" />
                {step < STEPS.length - 1 ? (
                  <Button
                    onClick={next}
                    className="rounded-xl bg-[#009940] hover:bg-[#007F35] text-white px-6 h-11 shadow-sm"
                    data-testid="wizard-next-button"
                  >
                    Siguiente <ArrowRight size={16} className="ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="rounded-xl bg-[#009940] hover:bg-[#007F35] text-white px-6 h-11 shadow-sm"
                    data-testid="wizard-submit-button"
                  >
                    {loading ? (
                      <><Loader2 size={16} className="mr-2 animate-spin" /> Enviando…</>
                    ) : (
                      <><Check size={16} className="mr-2" /> Enviar solicitud</>
                    )}
                  </Button>
                )}
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-4 text-center">
              Servicio gestionado por <strong className="text-[#009940]">Valoriza</strong> ·
              Ayuntamiento de <strong className="text-[#374B67]">Majadahonda</strong>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
